import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { PingSummary } from '../types/diagnostics';

interface Props {
  ping: PingSummary;
}

export const RTTChart: React.FC<Props> = ({ ping }) => {
  const data = ping.rtts.map((rtt, i) => ({
    seq: i + 1,
    rtt: Math.round(rtt * 100) / 100,
  }));

  return (
    <div className="bg-dark-900 rounded-xl p-6 border border-dark-700">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
          RTT Graph
        </h3>
        <div className="flex gap-4 text-xs text-slate-500">
          <span>Min: <span className="text-emerald-400">{ping.min.toFixed(1)}ms</span></span>
          <span>Avg: <span className="text-blue-400">{ping.avg.toFixed(1)}ms</span></span>
          <span>Max: <span className="text-red-400">{ping.max.toFixed(1)}ms</span></span>
          <span>P99: <span className="text-yellow-400">{ping.p99.toFixed(1)}ms</span></span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis
            dataKey="seq"
            stroke="#475569"
            fontSize={11}
            tickLine={false}
            label={{ value: 'Sequence', position: 'insideBottom', offset: -2, fill: '#64748b', fontSize: 10 }}
          />
          <YAxis
            stroke="#475569"
            fontSize={11}
            tickLine={false}
            label={{ value: 'ms', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }}
          />
          <Tooltip
            contentStyle={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            labelFormatter={(v) => `Ping #${v}`}
            formatter={(v: number) => [`${v.toFixed(2)} ms`, 'RTT']}
          />
          <ReferenceLine y={ping.avg} stroke="#3b82f6" strokeDasharray="5 5" strokeOpacity={0.5} />
          <ReferenceLine y={ping.p90} stroke="#f59e0b" strokeDasharray="3 3" strokeOpacity={0.3} />
          <Line
            type="monotone"
            dataKey="rtt"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: '#3b82f6', stroke: '#020617', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Stats bar */}
      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
        <StatBox label="Jitter" value={`${ping.jitter.toFixed(1)} ms`} color="text-purple-400" />
        <StatBox label="Loss" value={`${ping.lossPercent}%`} color={ping.lossPercent > 1 ? 'text-red-400' : 'text-emerald-400'} />
        <StatBox label="P50" value={`${ping.p50.toFixed(1)} ms`} color="text-blue-400" />
        <StatBox label="P90" value={`${ping.p90.toFixed(1)} ms`} color="text-yellow-400" />
      </div>
    </div>
  );
};

const StatBox: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div className="bg-dark-850 rounded-lg py-2 px-3">
    <div className="text-[10px] text-slate-500 uppercase">{label}</div>
    <div className={`text-sm font-semibold ${color}`}>{value}</div>
  </div>
);
